import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { DashboardComponent } from './dashboard/dashboard.component';
import { LogisticsDashboardComponent } from './dashboard/logistics-dashboard/logistics-dashboard.component';
import { TaxAccountancyDashboardComponent } from './dashboard/tax-accountancy-dashboard/tax-accountancy-dashboard.component';
import { FinancialServicesDashboardComponent } from './dashboard/financial-services-dashboard/financial-services-dashboard.component';
import { CompanyClustersComponent } from './company-clusters/company-clusters.component';
import { LogoutComponent } from './logout/logout.component';
import { LoginRegisterComponent } from './login-register/login-register.component';
import { SsoLoginComponent } from './sso-login/sso-login.component';
import { AccountComponent } from './account/account.component';
import { CrossSellComponent } from './cross-sell/cross-sell.component';
import { UploadCompaniesComponent } from './upload-companies/upload-companies.component';
import { OrganisationManagementComponent } from './organisation-management/organisation-management.component';
import { LearningPortalComponent } from './learning-portal/learning-portal.component';
import { AlertsComponent } from './alerts/alerts.component';
import { PropensityScoreComponent } from './propensity-score/propensity-score.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { ManageTeamsComponent } from './manage-teams/manage-teams.component';
import { UploadCompaniesV2Component } from './upload-companies-v2/upload-companies-v2.component';
import { SavedSearchesComponent } from './saved-searches/saved-searches.component';
import { ZendeskAuthComponent } from './zendesk-auth/zendesk-auth.component';
import { PurchasePlanComponent } from './purchase-plan/purchase-plan.component';
import { PlanDetailsComponent } from './plan-details/plan-details.component';
import { ZintGrowComponent } from './zint-grow/zint-grow.component';
import { ZintGrowSubscriptionsComponent } from './zint-grow-subscriptions/zint-grow-subscriptions.component';
import { AiPitchBuilderComponent } from './ai-pitch-builder/ai-pitch-builder.component';
import { ManageTerritoriesComponent } from './manage-territories/manage-territories.component';
import { CalendarIntegrationComponent } from './calendar-integration/calendar-integration.component';
import { ManageEmailsComponent } from './manage-emails/manage-emails.component';
import { NotificationsPageComponent } from './zint-grow/notifications-page/notifications-page.component';
import { DownloadLinkComponent } from './download-link/download-link.component';
import { AuthGuard } from '../services/auth.guard';
import { AiMonitoringGuard } from '../services/ai-monitoring.guard';
import { DashboardSkinGuard } from '../services/dashboard-skin.guard';
import { DashboardDefaultGuard } from '../services/dashboard-default.guard';
import { ProfileDefaultGuard } from '../services/profile-default.guard';
import { ProfileLogisticsGuard } from '../services/profile-logistics.guard';
import { ProfileTaxAccountancyGuard } from '../services/profile-tax-accountancy.guard';
import { ProfileFinancialServicesGuard } from '../services/profile-financial-services.guard';
import { CompanyProfilePageComponent } from './company-profile-page/company-profile-page.component';
import { LogisticsCompanyProfileComponent } from './logistics-company-profile/logistics-company-profile.component';
import { TaxAccountancyCompanyProfileComponent } from './tax-accountancy-company-profile/tax-accountancy-company-profile.component';
import { FinancialServicesCompanyProfileComponent } from './financial-services-company-profile/financial-services-company-profile.component';
import { CrossSellGraphComponent } from './cross-sell-graph/cross-sell-graph.component';
import { AiraComponent } from './aira/aira.component';
import { ManageQuotasComponent } from './manage-quotas/manage-quotas.component';
import { CrmComponent } from './crm/crm.component';
import { CrmPickerComponent } from './html-snippet-components/crm-picker/crm-picker.component';
import {ManageModuleComponent} from './magic-insights/manage-module/manage-module.component';

const routes: Routes = [
  {
    path: 'ai-monitoring',
    canActivate: [AuthGuard, AiMonitoringGuard],
    loadChildren: () =>
      import('./ai-monitoring/ai-monitoring.module').then(
        m => m.AiMonitoringModule
      ),
  },
  { path: 'login', component: LoginRegisterComponent },
  { path: 'sso-login', component: SsoLoginComponent },
  { path: 'register', component: LoginRegisterComponent },
  { path: 'reset_password', component: LoginRegisterComponent },
  { path: 'reset_password_confirm', component: LoginRegisterComponent },
  { path: 'confirm_account', component: LoginRegisterComponent },
  { path: 'request_new_activation_token', component: LoginRegisterComponent },
  { path: 'logout', component: LogoutComponent, canActivate: [AuthGuard] },
  { path: 'dashboard-home', component: DashboardComponent, canActivate: [AuthGuard, DashboardDefaultGuard] },
  {
    path: 'dashboard/logistics',
    component: LogisticsDashboardComponent,
    canActivate: [AuthGuard, DashboardSkinGuard],
    data: { dashboardSkin: 'logistics' },
  },
  {
    path: 'dashboard/tax-accountancy',
    component: TaxAccountancyDashboardComponent,
    canActivate: [AuthGuard, DashboardSkinGuard],
    data: { dashboardSkin: 'tax_accountancy' },
  },
  {
    path: 'dashboard/financial-services',
    component: FinancialServicesDashboardComponent,
    canActivate: [AuthGuard, DashboardSkinGuard],
    data: { dashboardSkin: 'financial_services' },
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard],
  },
  { path: 'account', component: AccountComponent, canActivate: [AuthGuard] },
  { path: 'zint-academy', component: LearningPortalComponent },
  {
    path: 'manage-team',
    component: OrganisationManagementComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'upload-companies',
    component: UploadCompaniesComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'cross-sell-opportunities',
    component: CrossSellComponent,
    canActivate: [AuthGuard],
  },
  { path: 'alerts', component: AlertsComponent, canActivate: [AuthGuard] },
  {
    path: 'propensity-score',
    component: PropensityScoreComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'crm',
    component: CrmPickerComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'crm/:crmType',
    component: CrmComponent,
    canActivate: [AuthGuard],
  },
  { path: 'crm-integrations', redirectTo: 'crm', pathMatch: 'full' },
  { path: 'salesforce', redirectTo: 'crm/salesforce', pathMatch: 'full' },
  { path: 'hubspot', redirectTo: 'crm/hubspot', pathMatch: 'full' },
  {
    path: 'company/:id',
    redirectTo: 'profile/:id',
    pathMatch: 'full',
  },
  {
    path: 'profile/:id',
    component: CompanyProfilePageComponent,
    canActivate: [AuthGuard, ProfileDefaultGuard],
  },
  {
    path: 'profile/:id/logistics',
    component: LogisticsCompanyProfileComponent,
    canActivate: [AuthGuard, ProfileLogisticsGuard],
    data: { profileSkin: 'logistics' },
  },
  {
    path: 'profile/:id/tax-accountancy',
    component: TaxAccountancyCompanyProfileComponent,
    canActivate: [AuthGuard, ProfileTaxAccountancyGuard],
    data: { profileSkin: 'tax_accountancy' },
  },
  {
    path: 'profile/:id/financial-services',
    component: FinancialServicesCompanyProfileComponent,
    canActivate: [AuthGuard, ProfileFinancialServicesGuard],
    data: { profileSkin: 'financial_services' },
  },
  {
    path: 'profile/:id/clusters',
    component: CompanyClustersComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'profile/:id/cross-sell',
    component: CrossSellGraphComponent,
    canActivate: [AuthGuard],
  },
  { path: 'privacy-policy', component: PrivacyPolicyComponent },
  {
    path: 'manage-organisation',
    component: ManageTeamsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'matcher/crm',
    component: UploadCompaniesV2Component,
    canActivate: [AuthGuard],
  },
  {
    path: 'matcher',
    component: UploadCompaniesV2Component,
    canActivate: [AuthGuard],
  },
  {
    path: 'saved-searches',
    component: SavedSearchesComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'knowledge-base-auth',
    component: ZendeskAuthComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'purchase-plan',
    component: PurchasePlanComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'plan-details',
    component: PlanDetailsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'grow',
    component: NotificationsPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'grow/subscriptions',
    component: ZintGrowSubscriptionsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'pitch-builder',
    component: AiPitchBuilderComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'manage-territories',
    component: ManageTerritoriesComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'calendar-integration',
    component: CalendarIntegrationComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'manage-emails',
    component: ManageEmailsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'notifications',
    component: ZintGrowComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'manage-quota',
    component: ManageQuotasComponent,
    canActivate: [AuthGuard],
  },
  { path: 'aira', component: AiraComponent, canActivate: [AuthGuard] },
  {
    path: 'download-link/:id',
    component: DownloadLinkComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'manage-magic-insights-module',
    component: ManageModuleComponent,
    canActivate: [AuthGuard],
  },
  { path: '**', redirectTo: '/dashboard-home' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
